import type { TranslationKey } from '../../i18n/en'
import type { EntityOutboxEntityType } from '../../offline/db'
import type { FeedLog, MortalityLog, WeightSampleLog } from '../../types/production'
import type { WaterDataRow } from '../../utils/waterTable'
import type { CrewModeStreamId } from './crewModeStreams'
import { defaultDayTimeZone, farmDayKey } from '../../utils/farmTime'
import { MORTALITY_CAUSE_LABEL_KEY } from '../../utils/mortalityByCause'

// Crew Mode pilot shell v0 — bundle E (operator "my entries today") +
// bundle G (waiting-to-sync count, folded in).
//
// PURE module. It takes rows already read from the four capture sources
// plus the current outbox snapshot, and produces the active operator's
// own entries since local midnight, newest first, each tagged with a
// four-state sync badge — and the count of those not yet synced.
//
// The four sources have three different shapes:
//   - feed / mortality / weightSample: `xLogApi.list` rows carrying
//     `capturedByActorId` and `recordedAt`.
//   - water: `waterTable.ts` `WaterDataRow` — `date` instead of
//     `recordedAt`, one non-null numeric field per Crew Mode capture, and
//     `capturedByActorId` only on rows written by the Crew Mode screen
//     (admin-app water rows have none and are correctly excluded).
//
// Charter O-1 / O-3: every emitted field is a raw value the operator
// entered (count, weight, parameter reading, feed item + kg). No rate,
// mean, biomass, cost, FCR, SFR, or any derived / aggregate figure is
// produced anywhere here — PRD §11 AC #9. Charter O-5: nothing here
// awaits the network; the caller reads local-first and passes rows in.

export type CrewTodaySyncState = 'local' | 'pending' | 'synced' | 'error'

/** One raw label/value pair shown on a "today" row. `value` is the raw
 *  entered figure as a display string; never a computed number. */
export type CrewTodayField = {
  labelKey: TranslationKey
  value: string
}

export type CrewTodayEntry = {
  id: string
  stream: CrewModeStreamId
  /** ISO timestamp the row was recorded at (local-day filtered already). */
  recordedAt: string
  fields: CrewTodayField[]
  syncState: CrewTodaySyncState
  /** Field Bundle v0 §3 item 3: where it was recorded ("Lot 1 · A3", water: "A3"). */
  locationLabel?: string
  /** Field Bundle v0 §3 item 8: recorded today for a past day ("registrado tarde"). */
  lateEntry?: boolean
}

export type CrewTodayOutboxEntry = {
  entityType: EntityOutboxEntityType
  entityId: string
  lastError?: string
}

export type OperatorTodayEntriesInput = {
  /** Active operator actorId, from `getCurrentOperatorActorId()`. When
   *  undefined (no operator session) the result is empty. */
  actorId: string | undefined
  /** Reference "now" for the local-midnight boundary. Defaults to new Date(). */
  now?: Date
  /** The farm's time zone for "today" (findings #85). Defaults to the farms' shared zone. */
  timeZone?: string
  /** Whether the device is online — decides pending vs local for a queued
   *  row. Defaults to `navigator.onLine`. */
  online?: boolean
  outbox: CrewTodayOutboxEntry[]
  water: WaterDataRow[]
  feed: FeedLog[]
  mortality: MortalityLog[]
  weightSample: WeightSampleLog[]
  /** Unit names by id, for the location label. */
  unitNames?: Record<string, string>
  /** Water rows are loaded per unit and carry no unit id themselves. */
  waterUnitIdByRowId?: Record<string, string>
}

export type OperatorTodayEntriesResult = {
  /** Newest first. */
  entries: CrewTodayEntry[]
  /** Count of `entries` not in the `synced` state (PRD §11 AC #7). */
  waitingToSyncCount: number
}

const STREAM_ENTITY_TYPE: Record<CrewModeStreamId, EntityOutboxEntityType> = {
  water: 'waterLog',
  feed: 'feedLog',
  mortality: 'mortalityLog',
  weightSample: 'weightSampleLog'
}

const WATER_FIELD_LABEL_KEY: Record<'temperature' | 'ph' | 'dissolvedOxygen' | 'salinity' | 'flow', TranslationKey> = {
  temperature: 'crewMode.water.parameter.temperature',
  ph: 'crewMode.water.parameter.ph',
  dissolvedOxygen: 'crewMode.water.parameter.dissolvedOxygen',
  salinity: 'crewMode.water.parameter.salinity',
  flow: 'crewMode.water.parameter.flow'
}

const WATER_FIELD_UNIT: Record<'temperature' | 'ph' | 'dissolvedOxygen' | 'salinity' | 'flow', string> = {
  temperature: '°C',
  ph: '',
  dissolvedOxygen: 'mg/L',
  salinity: 'ppt',
  flow: 'L/min'
}

// Findings #85: "today" is the farm's day in the farm's zone.
const isSameFarmDay = (isoOrDate: string, reference: Date, timeZone: string): boolean => {
  const day = farmDayKey(isoOrDate, timeZone)
  return day !== null && day === farmDayKey(reference, timeZone)
}

const buildOutboxIndex = (outbox: CrewTodayOutboxEntry[]): Map<string, { blocked: boolean }> => {
  const index = new Map<string, { blocked: boolean }>()
  for (const entry of outbox) {
    const key = `${entry.entityType}:${entry.entityId}`
    const blocked = typeof entry.lastError === 'string' && entry.lastError.trim().length > 0
    const existing = index.get(key)
    index.set(key, { blocked: blocked || Boolean(existing?.blocked) })
  }
  return index
}

const resolveSyncState = (
  stream: CrewModeStreamId,
  id: string,
  outboxIndex: Map<string, { blocked: boolean }>,
  online: boolean
): CrewTodaySyncState => {
  const queued = outboxIndex.get(`${STREAM_ENTITY_TYPE[stream]}:${id}`)
  if (!queued) return 'synced'
  if (queued.blocked) return 'error'
  return online ? 'pending' : 'local'
}

const trimmedString = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  return String(value).trim()
}

const normalizeWaterRow = (row: WaterDataRow): Omit<CrewTodayEntry, 'syncState'> | null => {
  const fields: CrewTodayField[] = []
  for (const parameter of ['temperature', 'ph', 'dissolvedOxygen', 'salinity'] as const) {
    const raw = row[parameter]
    if (raw === null || raw === undefined) continue
    const unit = WATER_FIELD_UNIT[parameter]
    fields.push({
      labelKey: WATER_FIELD_LABEL_KEY[parameter],
      value: unit ? `${raw} ${unit}` : String(raw)
    })
  }
  // Hatchery v0 (#84): flow travels in metadata.
  const flow = (row.metadata as Record<string, unknown> | undefined)?.flowLpm
  if (typeof flow === 'number' && Number.isFinite(flow)) {
    fields.push({ labelKey: WATER_FIELD_LABEL_KEY.flow, value: `${flow} ${WATER_FIELD_UNIT.flow}` })
  }
  if (fields.length === 0) return null
  return { id: row.id, stream: 'water', recordedAt: row.date, fields }
}

const normalizeFeedRow = (row: FeedLog): Omit<CrewTodayEntry, 'syncState'> | null => {
  const fields: CrewTodayField[] = []
  const feedType = trimmedString(row.feedType)
  if (feedType) fields.push({ labelKey: 'crewMode.home.today.field.feedType', value: feedType })
  const feedKg = trimmedString(row.feedKg)
  if (feedKg) fields.push({ labelKey: 'crewMode.home.today.field.feedKg', value: feedKg })
  if (fields.length === 0) return null
  return { id: row.id, stream: 'feed', recordedAt: row.recordedAt, fields }
}

const NOT_CHECKED_LABEL_KEY: Record<string, TranslationKey> = {
  weather: 'crewMode.mortality.notChecked.weather',
  equipment: 'crewMode.mortality.notChecked.equipment',
  no_staff: 'crewMode.mortality.notChecked.noStaff',
  other: 'crewMode.mortality.notChecked.other'
}

const normalizeMortalityRow = (row: MortalityLog): Omit<CrewTodayEntry, 'syncState'> | null => {
  const metadata = (row.metadata ?? {}) as Record<string, unknown>
  // Field Bundle v0 §3 item 8: an explicit zero and a "not checked" record
  // read as what they are, never as a plain count.
  if (metadata.notChecked === true) {
    const reason = NOT_CHECKED_LABEL_KEY[String(metadata.notCheckedReason ?? '')]
    return {
      id: row.id,
      stream: 'mortality',
      recordedAt: row.recordedAt,
      fields: [{ labelKey: 'crewMode.home.today.field.notChecked', value: '' }, ...(reason ? [{ labelKey: reason, value: '' }] : [])]
    }
  }
  if (metadata.noMortality === true) {
    return {
      id: row.id,
      stream: 'mortality',
      recordedAt: row.recordedAt,
      fields: [{ labelKey: 'crewMode.mortality.summary.none', value: '' }]
    }
  }
  const count = trimmedString(row.mortalityCount)
  if (!count) return null
  const cause = MORTALITY_CAUSE_LABEL_KEY[String(metadata.observedCause ?? '')]
  return {
    id: row.id,
    stream: 'mortality',
    recordedAt: row.recordedAt,
    fields: [
      { labelKey: 'crewMode.home.today.field.mortalityCount', value: count },
      ...(cause ? [{ labelKey: cause, value: '' }] : [])
    ]
  }
}

const isLateEntry = (row: { metadata?: Record<string, unknown> }): boolean =>
  (row.metadata as Record<string, unknown> | undefined)?.lateEntry === true

const normalizeWeightSampleRow = (row: WeightSampleLog): Omit<CrewTodayEntry, 'syncState'> | null => {
  const weight = trimmedString(row.weightGrams)
  if (!weight) return null
  const fields: CrewTodayField[] = [
    { labelKey: 'crewMode.home.today.field.weightGrams', value: weight }
  ]
  if (row.sampleCount !== null && row.sampleCount !== undefined) {
    fields.push({ labelKey: 'crewMode.home.today.field.sampleCount', value: String(row.sampleCount) })
  }
  return { id: row.id, stream: 'weightSample', recordedAt: row.recordedAt, fields }
}

/**
 * The active operator's own entries from today, newest first, plus the
 * count still waiting to sync.
 */
export const buildOperatorTodayEntries = (
  input: OperatorTodayEntriesInput
): OperatorTodayEntriesResult => {
  const actorId = trimmedString(input.actorId)
  if (!actorId) {
    return { entries: [], waitingToSyncCount: 0 }
  }

  const now = input.now ?? new Date()
  const zone = input.timeZone ?? defaultDayTimeZone()
  const online = input.online ?? (typeof navigator !== 'undefined' ? navigator.onLine : true)
  const outboxIndex = buildOutboxIndex(input.outbox)

  const matchesOperatorToday = (capturedByActorId: string | undefined, when: string): boolean => (
    trimmedString(capturedByActorId) === actorId && isSameFarmDay(when, now, zone)
  )
  // A late entry (Ayer / Hace 2 días) belongs to "today's work" when it was
  // entered today, even though it is recorded on a past day.
  const matchesLateToday = (row: { capturedByActorId?: string; createdAt?: string; metadata?: Record<string, unknown> }): boolean => (
    isLateEntry(row) && trimmedString(row.capturedByActorId) === actorId && Boolean(row.createdAt) && isSameFarmDay(String(row.createdAt), now, zone)
  )

  const unitNames = input.unitNames ?? {}
  const lotLocation = (row: { lot?: string; productionUnitId?: string }): string | undefined => {
    const unit = row.productionUnitId ? unitNames[row.productionUnitId] : undefined
    const lot = trimmedString(row.lot)
    if (lot && unit) return `${lot} · ${unit}`
    return lot || unit || undefined
  }

  const normalized: Array<Omit<CrewTodayEntry, 'syncState'>> = []

  for (const row of input.water) {
    if (!matchesOperatorToday(row.capturedByActorId, row.date)) continue
    const entry = normalizeWaterRow(row)
    const unitId = input.waterUnitIdByRowId?.[row.id]
    if (entry) normalized.push({ ...entry, locationLabel: unitId ? unitNames[unitId] : undefined })
  }
  for (const row of input.feed) {
    if (!matchesOperatorToday(row.capturedByActorId, row.recordedAt)) continue
    const entry = normalizeFeedRow(row)
    if (entry) normalized.push({ ...entry, locationLabel: lotLocation(row) })
  }
  for (const row of input.mortality) {
    const late = matchesLateToday(row)
    if (!late && !matchesOperatorToday(row.capturedByActorId, row.recordedAt)) continue
    const entry = normalizeMortalityRow(row)
    if (entry) normalized.push({ ...entry, locationLabel: lotLocation(row), ...(late ? { lateEntry: true } : {}) })
  }
  for (const row of input.weightSample) {
    if (!matchesOperatorToday(row.capturedByActorId, row.recordedAt)) continue
    const entry = normalizeWeightSampleRow(row)
    if (entry) normalized.push({ ...entry, locationLabel: lotLocation(row) })
  }

  const entries: CrewTodayEntry[] = normalized
    .map((entry) => ({
      ...entry,
      syncState: resolveSyncState(entry.stream, entry.id, outboxIndex, online)
    }))
    .sort((left, right) => {
      const delta = new Date(right.recordedAt).getTime() - new Date(left.recordedAt).getTime()
      if (delta !== 0) return delta
      // Stable, deterministic tiebreak for rows sharing a timestamp.
      return left.id.localeCompare(right.id)
    })

  const waitingToSyncCount = entries.filter((entry) => entry.syncState !== 'synced').length

  return { entries, waitingToSyncCount }
}
